import React, { useCallback, useMemo, useState } from "react"

import { useEvent } from "shared/lib/useEvent"
import { Floor } from "shared/types/Floor"
import { ProductPosition } from "shared/types/ProductPosition"
import { isProjectProductPosition, isProjectRoom } from "shared/types/Project"
import { Room } from "shared/types/Room"
import { ProductPositionAddon } from "shared/types/__generated__/graphql"
import { RowRendererProps } from "shared/ui/table/types"

import { reconcileRoomSelection } from "../lib/reconcileRoomSelection"
import { MusterheftExportOverlayModel } from "./useMusterheftExportOverlayModel"
import {
  MusterheftExportChild,
  useMusterheftExportTabState
} from "./useMusterheftExportTabState"

export interface MusterheftExportOverlayViewModel {
  projectID: string
  isLoading: boolean
  floors: Floor[]
  unassignedRoomsCount: number
  unassignedProductsCount: number
  visibleChildren: MusterheftExportChild[]
  activeTabID: string | null
  onSelectTab: (id: string) => void
  selectedItems: string[]
  onSelectionChange: (ids: string[]) => void
  exportAll: boolean
  onToggleExportAll: (value: boolean) => void
  isExportDisabled: boolean
  onSubmit: (event?: React.FormEvent<HTMLFormElement>) => void
  onCancel: () => void
  RowRenderer?: React.ComponentType<
    RowRendererProps<ProductPosition | ProductPositionAddon | Room>
  >
  columnPreset: "selection"
  isItemCollapsed: (item: { id: string; __typename?: string }) => boolean
  onItemCollapse: (id: string) => void
  onCollapseAll: () => void
  onExpandAll: () => void
  isAllSelected: boolean
  isSomeSelected: boolean
  onSelectAll: (checked: boolean) => void
}

export function useMusterheftExportOverlayViewModel(
  model: MusterheftExportOverlayModel
): MusterheftExportOverlayViewModel {
  const {
    projectID,
    projectChildren,
    isLoading,
    onCancel,
    onExport,
    RowRenderer
  } = model

  const [selectedItems, setSelectedItems] = useState<string[]>([])
  const [requestedTabID, setRequestedTabID] = useState<string | null>(null)
  const [exportAll, setExportAll] = useState(true)

  const {
    floors,
    unassignedRoomsCount,
    unassignedProductsCount,
    resolvedTabID,
    visibleChildren
  } = useMusterheftExportTabState({
    projectChildren,
    activeTabID: requestedTabID
  })

  // Build the full set of rooms from ALL data (all tabs) so the cascade and
  // submit filter work regardless of which tab is active.
  // Covers: unassigned rooms (top-level in projectChildren) + rooms inside floors.
  const allRooms = useMemo(
    () => [
      ...projectChildren.filter(isProjectRoom),
      ...floors.flatMap(f => f.children ?? [])
    ],
    [projectChildren, floors]
  )

  // Build a set of all room IDs for filtering before submit.
  const allRoomIDs = useMemo(() => new Set(allRooms.map(r => r.id)), [allRooms])

  const onSelectionChange = useEvent((ids: string[]) => {
    setSelectedItems(prev => reconcileRoomSelection(prev, ids, allRooms))
  })
  const onSelectTab = useEvent((id: string) => setRequestedTabID(id))
  const onToggleExportAll = useEvent((value: boolean) => setExportAll(value))

  // Only product positions are exported; room IDs live in the selection to
  // drive the room checkbox but are not sent.
  const selectedPositionIDs = useMemo(
    () => selectedItems.filter(id => !allRoomIDs.has(id)),
    [selectedItems, allRoomIDs]
  )

  const [collapsedRoomIDs, setCollapsedRoomIDs] = useState<Set<string>>(
    () => new Set()
  )

  // Product positions directly visible in the active tab (rooms are excluded;
  // their children count toward selection through the room cascade).
  const visiblePositionIDs = useMemo(() => {
    const ids: string[] = []
    visibleChildren.forEach(child => {
      if (isProjectRoom(child)) {
        ;(child.children ?? []).forEach(c => ids.push(c.id))
      } else if (isProjectProductPosition(child)) {
        ids.push(child.id)
      }
    })
    return ids
  }, [visibleChildren])

  const visibleRoomIDs = useMemo(
    () => visibleChildren.filter(isProjectRoom).map(room => room.id),
    [visibleChildren]
  )

  const selectedSet = useMemo(() => new Set(selectedItems), [selectedItems])

  const selectedVisibleCount = useMemo(
    () => visiblePositionIDs.filter(id => selectedSet.has(id)).length,
    [visiblePositionIDs, selectedSet]
  )

  const isAllSelected =
    visiblePositionIDs.length > 0 &&
    selectedVisibleCount === visiblePositionIDs.length
  const isSomeSelected =
    selectedVisibleCount > 0 && selectedVisibleCount < visiblePositionIDs.length

  const onSelectAll = useEvent((checked: boolean) => {
    setSelectedItems(prev => {
      const next = new Set(prev)
      if (checked) {
        visiblePositionIDs.forEach(id => next.add(id))
        // A room is selected iff all its children are selected.
        visibleChildren.forEach(child => {
          if (
            isProjectRoom(child) &&
            (child.children?.length ?? 0) > 0 &&
            (child.children ?? []).every(c => next.has(c.id))
          ) {
            next.add(child.id)
          }
        })
      } else {
        visiblePositionIDs.forEach(id => next.delete(id))
        visibleRoomIDs.forEach(id => next.delete(id))
      }
      return Array.from(next)
    })
  })

  const isItemCollapsed = useCallback(
    (item: { id: string; __typename?: string }) =>
      collapsedRoomIDs.has(item.id),
    [collapsedRoomIDs]
  )

  const onItemCollapse = useEvent((id: string) => {
    setCollapsedRoomIDs(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  })

  const onCollapseAll = useEvent(() => {
    setCollapsedRoomIDs(new Set(visibleRoomIDs))
  })

  const onExpandAll = useEvent(() => {
    setCollapsedRoomIDs(new Set())
  })

  const isExportDisabled = useMemo(
    () => !exportAll && selectedPositionIDs.length === 0,
    [exportAll, selectedPositionIDs]
  )

  const onSubmit = useEvent((event?: React.FormEvent<HTMLFormElement>) => {
    event?.preventDefault()
    onExport(
      exportAll
        ? { exportAll: true }
        : { exportAll: false, positionIDs: selectedPositionIDs }
    )
  })

  return {
    projectID,
    isLoading,
    floors,
    unassignedRoomsCount,
    unassignedProductsCount,
    visibleChildren,
    activeTabID: resolvedTabID,
    onSelectTab,
    selectedItems,
    onSelectionChange,
    exportAll,
    onToggleExportAll,
    isExportDisabled,
    onSubmit,
    onCancel,
    RowRenderer,
    columnPreset: "selection",
    isItemCollapsed,
    onItemCollapse,
    onCollapseAll,
    onExpandAll,
    isAllSelected,
    isSomeSelected,
    onSelectAll
  }
}
